import type { FlowTriggerNodeConfig } from '@reflex/flows';

/**
 * One entry of the org-wide event catalog served by `GET /flows/event-catalog`
 * (see `FlowModelRegistry.getEventCatalog`). Powers the editor's event picker
 * and the run dialog's form generation / JSON paste validation.
 */
export interface FlowEventCatalogEntry {
  source: string;
  event: string;
  plugin: string;
  description: string;
  /** Enumerated `ReflexEvent.action` values, when the event carries one. */
  actions?: string[];
  /** Payload fields that pin one object / one container (see plugin-api `FlowEventModelDefinition`). */
  identityPaths?: string[];
  scopePaths?: string[];
  /** Name of the payload model (cross-references the capabilities view). */
  payloadModel: string;
  /** JSON Schema of the payload; null when the model declares none. */
  payloadJsonSchema: Record<string, unknown> | null;
}

/** Stable key for one catalog entry. */
export function eventKey(entry: Pick<FlowEventCatalogEntry, 'source' | 'event'>): string {
  return `${entry.source}/${entry.event}`;
}

/** Group entries by source for the picker, sources sorted alphabetically. */
export function groupEventCatalogBySource(
  entries: FlowEventCatalogEntry[],
): Array<{ source: string; entries: FlowEventCatalogEntry[] }> {
  const groups = new Map<string, FlowEventCatalogEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.source);
    if (list) list.push(entry);
    else groups.set(entry.source, [entry]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([source, list]) => ({ source, entries: list }));
}

/** Find the catalog entry an event-kind trigger node subscribes to. */
export function findCatalogEntry(
  entries: FlowEventCatalogEntry[],
  config: { source: string; event: string },
): FlowEventCatalogEntry | undefined {
  return entries.find((e) => e.source === config.source && e.event === config.event);
}

/**
 * Resolve the `flowTriggerRenderers` registry key for a trigger node.
 * Plugin web manifests historically register renderers under the legacy
 * trigger type name (`sentry_issue`), which equals the normalized event
 * name — so for event triggers we try `source:event` first and fall back
 * to the bare event name.
 */
export function rendererKeyForTrigger(config: FlowTriggerNodeConfig): string[] {
  switch (config.kind) {
    case 'event':
      return [`${config.source}:${config.event}`, config.event];
    case 'cron':
      return ['cron'];
    case 'manual':
      return ['manual'];
    case 'service':
      // Most specific first: a plugin may register a renderer keyed by the
      // service id, falling back to the generic `service` key.
      return [`service:${config.service}`, 'service'];
    default:
      // Legacy or otherwise unrecognized trigger configs (e.g. flows persisted
      // before triggers became graph nodes, or a run pinned to an older
      // snapshot) can carry a `kind` outside the current union. Return no
      // renderer keys so callers fall back to their generic trigger rendering
      // instead of crashing on `triggerKeys.map(...)`.
      return [];
  }
}
